import { useCallback, useEffect, useRef, useState } from 'react';

/** Steady reveal rate tuned for ~50–105 char server chunks every ~430–880ms. */
const STREAMING_CHARS_PER_SECOND = 165;
const MAX_CHARS_PER_FRAME = 3;
const MIN_CHUNK_SPREAD_MS = 360;
const FIRST_PAINT_CHARS = 2;

export function computeRevealCount(
  elapsedMs: number,
  allowedLag: number
): number {
  if (allowedLag <= 0) return 0;

  const timeBudget = Math.max(
    1,
    Math.floor((elapsedMs / 1000) * STREAMING_CHARS_PER_SECOND)
  );

  return Math.min(timeBudget, MAX_CHARS_PER_FRAME, allowedLag);
}

export function computeSpreadProgress(elapsedSinceChunkMs: number): number {
  return Math.min(1, elapsedSinceChunkMs / MIN_CHUNK_SPREAD_MS);
}

export function computeSpreadMaxIndex(
  currentIndex: number,
  targetLength: number,
  spread: {
    chunkStartIndex: number;
    chunkEndIndex: number;
    arrivedAt: number;
  },
  now: number
): number {
  const { chunkStartIndex, chunkEndIndex, arrivedAt } = spread;
  const delta = chunkEndIndex - chunkStartIndex;
  const progress = computeSpreadProgress(now - arrivedAt);

  if (currentIndex < chunkStartIndex) {
    return Math.min(targetLength, chunkStartIndex);
  }

  if (currentIndex < chunkEndIndex) {
    const spreadIndex =
      chunkStartIndex + Math.floor(delta * progress) + MAX_CHARS_PER_FRAME;
    return Math.min(targetLength, chunkEndIndex, spreadIndex);
  }

  return targetLength;
}

type ChunkSpreadState = {
  chunkStartIndex: number;
  chunkEndIndex: number;
  arrivedAt: number;
};

export function useStreamingTypewriter(isActive: boolean) {
  const [displayedText, setDisplayedText] = useState('');
  const targetRef = useRef('');
  const indexRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastTickRef = useRef(0);
  const chunkSpreadRef = useRef<ChunkSpreadState | null>(null);

  const cancelAnimation = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const publishDisplayedText = useCallback((index: number) => {
    indexRef.current = index;
    setDisplayedText(targetRef.current.slice(0, index));
  }, []);

  const getMaxRevealIndex = useCallback((index: number, targetLength: number, now: number) => {
    const spread = chunkSpreadRef.current;
    if (!spread) return targetLength;

    return computeSpreadMaxIndex(index, targetLength, spread, now);
  }, []);

  const tick = useCallback(
    (timestamp: number) => {
      const target = targetRef.current;
      const index = indexRef.current;

      if (index >= target.length) {
        rafRef.current = null;
        return;
      }

      const elapsedMs =
        lastTickRef.current === 0 ? 16 : timestamp - lastTickRef.current;

      if (elapsedMs >= 12) {
        lastTickRef.current = timestamp;

        const maxIndex = getMaxRevealIndex(index, target.length, timestamp);
        const allowedLag = maxIndex - index;
        const revealCount = computeRevealCount(elapsedMs, allowedLag);

        if (revealCount > 0) {
          publishDisplayedText(Math.min(index + revealCount, target.length));
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    },
    [getMaxRevealIndex, publishDisplayedText]
  );

  const ensureAnimation = useCallback(() => {
    if (rafRef.current !== null) return;
    lastTickRef.current = 0;
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const setTarget = useCallback(
    (text: string) => {
      const previousTarget = targetRef.current;
      targetRef.current = text;

      if (indexRef.current > text.length) {
        publishDisplayedText(text.length);
        return;
      }

      const delta = text.length - previousTarget.length;
      if (delta > 0) {
        chunkSpreadRef.current = {
          chunkStartIndex: previousTarget.length,
          chunkEndIndex: text.length,
          arrivedAt: performance.now(),
        };
      }

      if (
        isActive &&
        indexRef.current === 0 &&
        text.length > 0 &&
        previousTarget.length === 0
      ) {
        publishDisplayedText(Math.min(FIRST_PAINT_CHARS, text.length));
      }

      if (indexRef.current < text.length && isActive) {
        ensureAnimation();
      }
    },
    [ensureAnimation, isActive, publishDisplayedText]
  );

  const flush = useCallback(() => {
    cancelAnimation();
    chunkSpreadRef.current = null;
    publishDisplayedText(targetRef.current.length);
  }, [cancelAnimation, publishDisplayedText]);

  const reset = useCallback(() => {
    cancelAnimation();
    targetRef.current = '';
    indexRef.current = 0;
    chunkSpreadRef.current = null;
    setDisplayedText('');
  }, [cancelAnimation]);

  useEffect(() => {
    if (!isActive) {
      flush();
    }
  }, [flush, isActive]);

  useEffect(() => cancelAnimation, [cancelAnimation]);

  return { displayedText, setTarget, flush, reset };
};
